import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import theme from 'shared/styles/theme';

const AddressConfirmation = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('personalInfoStrings');
  const { result, fetching, error, refetch } = useGetProfileInfo();
  const { setSnackBarMessage } = useContext(GlobalUIContext);
  const dismissToTab = useDismissToRootTab();
  const customerInfo = result?.customerInfo;
  const address = customerInfo?.address;
  const addressLine1 = address?.addressLine1;
  const addressLine2 = address?.addressLine2;
  const city = address?.city;
  const state = address?.state;
  const zipCode = address?.zip;

  const { segmentClient } = useAnalyticsConfiguration();

  const handleConfirmAddress = () => {
    dismissToTab(Tabs.HOME);
    return setSnackBarMessage?.(
      translatedText('addressTranslations.successMessage'),
    );
  };

  const handleEditAddress = () => {
    router.push({
      pathname: '/myvaro/personal-details/change-address',
      params: { fromConfirmAddress: 'true' },
    });
  };

  useEffect(() => {
    if (error) {
      void segmentClient.logEvent('Confirm Address Error Encountered ', {
        category: 'profile_settings',
        subcategory: 'mastercard',
        error_name: `${error.name}`,
      });
    }
  }, [error, segmentClient]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: !error,
          headerBackVisible: false,
          headerTransparent: false,
          title: '',
          headerBackButtonMenuEnabled: !error,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={
                <Icon
                  iconKey="CLOSE"
                  iconSize="MD"
                  iconColor={theme.color.text.primary}
                  iconAccessibilityLabel="Close Screen"
                />
              }
              primaryOnPress={() => {
                dismissToTab(Tabs.HOME);
              }}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText(
                'addressTranslations.error.title',
              )}
              messageBody={translatedText('addressTranslations.error.message')}
              primaryButton={{
                title: translatedText('addressTranslations.error.button'),
                onPress: () => {
                  refetch();
                },
              }}
              secondaryButton={{
                title: translatedText('addressTranslations.error.closeButton'),
                onPress: () => dismissToTab(Tabs.HOME),
              }}
              screenAnalyticsEventKey="confirm-address-error"
            />
          ) : null
        }
      />
      {!error && !fetching ? (
        <ProductIntroScreen
          visual="KEYS_A"
          headlineText={translatedText('addressTranslations.headlineText')}
          headlineTextStyle="title"
          headlineTextAlign="flex-start"
          bodyContent={
            <View style={styles.bodyContainer}>
              <View style={styles.bodyTitle}>
                <BodyBaseText numberOfLines={3} ellipsizeMode="tail">
                  {translatedText('addressTranslations.bodyTitle')}
                </BodyBaseText>
              </View>
              <View style={styles.bodySectionContainer}>
                <View style={styles.addressContainer}>
                  <View style={styles.addressIconContainer}>
                    <Icon iconKey="LOCATION" iconSize="MD" />
                  </View>
                  <View style={styles.addressTextContainer}>
                    <BodyXSText
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={styles.bodyListHeader}
                    >
                      {translatedText('addressTranslations.homeAddress')}
                    </BodyXSText>
                    <BodyBaseText numberOfLines={1} ellipsizeMode="tail">
                      {addressLine1}
                    </BodyBaseText>
                    {addressLine2 ? (
                      <BodyBaseText numberOfLines={1} ellipsizeMode="tail">
                        {addressLine2}
                      </BodyBaseText>
                    ) : null}
                    <BodyBaseText
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >{`${city}, ${state} ${zipCode}`}</BodyBaseText>
                  </View>
                </View>
              </View>
            </View>
          }
          primaryButton={{
            label: translatedText('addressTranslations.confirmAddressButton'),
            onPress: handleConfirmAddress,
            analyticsId: 'confirmClicked',
          }}
          secondaryButton={{
            label: translatedText('addressTranslations.editAddressButton'),
            onPress: handleEditAddress,
            analyticsId: 'editAddressClicked',
          }}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  addressContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  addressIconContainer: {
    justifyContent: 'center',
    marginRight: theme.spacing.s,
  },
  addressTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  bodyContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  bodyListHeader: {
    color: theme.color.text.secondary,
  },
  bodySectionContainer: {
    paddingTop: theme.spacing.m,
  },
  bodyTitle: {
    marginTop: -theme.spacing.s,
    paddingBottom: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingTop: theme.spacing.l,
  },
});

export default AddressConfirmation;
